/**
 * Admin Products Management Page
 * Full CRUD: Add, Delete, Variants, Images, Stock with PIN, Pricing System with Cost+Markup
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Link2, ArrowLeft, Package, Search, RefreshCw, Plus, Trash2, X, FileUp, Download,
  DollarSign, Tag, Layers, History, Upload, Minus, AlertTriangle, Camera, ShoppingBag, Scale, Truck, Gavel, Clock, Edit2, Video
} from 'lucide-react';
import { Card } from '../components/ui/card';
import ProductVideoUpload from '../components/ProductVideoUpload';

const AdminProductsManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productVideos, setProductVideos] = useState([]);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportData, setBulkImportData] = useState({ file: null, preview: [], importing: false, results: null });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  
  const [newProduct, setNewProduct] = useState({
    cost_price: '', 
    markup_percentage: 45, 
    name: '', 
    description: '', 
    category: '', 
    price: '', 
    original_price: '', 
    is_liantiao_product: true, 
    is_wholesale_eligible: false,
    sku: '', 
    stock_quantity: 0, 
    startup_quantity: 0, 
    is_discount_eligible: true,
    free_shipping: false,
    flat_rate_shipping: '',
    images: [],
    weight_lbs: '',
    sale_mode: 'FIXED_PRICE',
    condition: 'NEW',
    auction_start_at: '',
    auction_end_at: '',
    auction_min_bid_jmd: '',
    auction_min_increment_jmd: '100',
    buy_now_price_jmd: ''
  });
  
  const [stockForm, setStockForm] = useState({ quantity: '', reason: '', pin: '', isAdding: true });
  const [variantForm, setVariantForm] = useState({ name: '', price: '', stock_quantity: 0, sku: '', pin: '', size: '', imagePreview: null, imageData: null });

  // Wholesale discount tiers
  const WHOLESALE_TIERS = [
    { qty: 3, discount: 10 },
    { qty: 9, discount: 15 },
    { qty: 18, discount: 25 }
  ];

  useEffect(() => { fetchProducts(); fetchCategories(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/products/');
      setProducts(response.data?.products || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/products/categories');
      setCategories(response.data?.categories || []);
    } catch (error) { console.error(error); }
  };

  // Calculate selling price from cost and markup
  const calculateSellingPrice = (cost, markup) => {
    const costNum = parseFloat(cost) || 0;
    const markupNum = parseFloat(markup) || 45;
    return Math.round(costNum * (1 + markupNum / 100) * 100) / 100;
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.category || !newProduct.cost_price) {
      toast.error('Name, category, and cost price are required'); return;
    }
    setActionLoading('add');
    try {
      const calculatedPrice = calculateSellingPrice(newProduct.cost_price, newProduct.markup_percentage);
      const payload = { 
        ...newProduct, 
        cost_price: parseFloat(newProduct.cost_price),
        price: calculatedPrice,
        markup_percentage: parseFloat(newProduct.markup_percentage) || 45,
        original_price: newProduct.original_price ? parseFloat(newProduct.original_price) : null,
        stock_quantity: parseInt(newProduct.stock_quantity) || 0,
        startup_quantity: parseInt(newProduct.startup_quantity) || parseInt(newProduct.stock_quantity) || 0,
        weight_lbs: newProduct.weight_lbs ? parseFloat(newProduct.weight_lbs) : null,
        sale_mode: newProduct.sale_mode || 'FIXED_PRICE',
        condition: newProduct.condition || 'NEW',
        auction_enabled: newProduct.sale_mode === 'AUCTION' || newProduct.sale_mode === 'FIXED_OR_AUCTION',
        auction_start_at: newProduct.auction_start_at ? new Date(newProduct.auction_start_at).toISOString() : null,
        auction_end_at: newProduct.auction_end_at ? new Date(newProduct.auction_end_at).toISOString() : null,
        auction_min_bid_jmd: newProduct.auction_min_bid_jmd ? parseFloat(newProduct.auction_min_bid_jmd) : null,
        auction_min_increment_jmd: newProduct.auction_min_increment_jmd ? parseFloat(newProduct.auction_min_increment_jmd) : 100,
        buy_now_price_jmd: newProduct.buy_now_price_jmd ? parseFloat(newProduct.buy_now_price_jmd) : null,
        free_shipping: newProduct.free_shipping || false,
        flat_rate_shipping: newProduct.flat_rate_shipping ? parseFloat(newProduct.flat_rate_shipping) : null
      };
      let response;
      if (editingProduct) {
        response = await api.put(`/admin/products/${editingProduct.id}`, payload);
        toast.success('Product updated successfully!');
      } else {
        response = await api.post('/admin/products/', payload);
        toast.success(`Product created! SKU: ${response.data.sku} | Selling: J$${response.data.selling_price}`);
      }
      setShowAddModal(false);
      setEditingProduct(null);
      setProductVideos([]);
      setNewProduct({ 
        cost_price: '', markup_percentage: 45, name: '', description: '', category: '', 
        price: '', original_price: '', is_liantiao_product: true, is_wholesale_eligible: false, images: [],
        sku: '', stock_quantity: 0, startup_quantity: 0, is_discount_eligible: true, free_shipping: false, weight_lbs: '',
        sale_mode: 'FIXED_PRICE', condition: 'NEW', auction_start_at: '', auction_end_at: '',
        auction_min_bid_jmd: '', auction_min_increment_jmd: '100', buy_now_price_jmd: ''
      });
      fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add product'); }
    finally { setActionLoading(null); }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductVideos(product.videos || []);
    setNewProduct({
      cost_price: product.cost_price?.toString() || '',
      markup_percentage: product.markup_percentage || 45,
      name: product.name || product.title || '',
      description: product.description || '',
      category: product.category || '',
      price: product.price?.toString() || '',
      original_price: product.original_price?.toString() || '',
      is_liantiao_product: product.is_liantiao_product !== false,
      is_wholesale_eligible: product.is_wholesale_eligible || false,
      sku: product.sku || '',
      stock_quantity: product.stock_quantity || 0,
      startup_quantity: product.startup_quantity || 0,
      is_discount_eligible: product.is_discount_eligible !== false,
      free_shipping: product.free_shipping || false,
      flat_rate_shipping: product.flat_rate_shipping || '',
      images: product.images || [],
      weight_lbs: product.weight_lbs?.toString() || '',
      sale_mode: product.sale_mode || 'FIXED_PRICE',
      condition: product.condition || 'NEW',
      auction_start_at: product.auction_start_at ? new Date(product.auction_start_at).toISOString().slice(0, 16) : '',
      auction_end_at: product.auction_end_at ? new Date(product.auction_end_at).toISOString().slice(0, 16) : '',
      auction_min_bid_jmd: product.auction_min_bid_jmd?.toString() || '',
      auction_min_increment_jmd: product.auction_min_increment_jmd?.toString() || '100',
      buy_now_price_jmd: product.buy_now_price_jmd?.toString() || ''
    });
    setShowAddModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    const pin = prompt('Enter your PIN to confirm deletion:');
    if (!pin) return;
    const reason = prompt('Enter reason for deleting this product:');
    if (!reason) return;
    setActionLoading(productId);
    try {
      await api.delete(`/admin/products/${productId}`, { data: { pin, reason } });
      toast.success('Product deleted'); fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete product'); }
    finally { setActionLoading(null); }
  };

  const openStockModal = (product, isAdding = true) => {
    setSelectedProduct(product);
    setStockForm({ quantity: '', reason: '', pin: '', isAdding });
    setShowStockModal(true);
  };

  const handleStockAdjust = async () => {
    if (!stockForm.quantity || !stockForm.reason || !stockForm.pin) {
      toast.error('All fields are required'); return;
    }
    setActionLoading('stock');
    try {
      const quantity = stockForm.isAdding ? parseInt(stockForm.quantity) : -parseInt(stockForm.quantity);
      await api.post(`/admin/products/${selectedProduct.id}/stock`, { quantity, reason: stockForm.reason, pin: stockForm.pin });
      toast.success('Stock adjusted successfully');
      setShowStockModal(false); fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to adjust stock'); }
    finally { setActionLoading(null); }
  };

  const openVariantModal = (product) => {
    setSelectedProduct(product);
    setVariantForm({ name: '', price: '', stock_quantity: 0, sku: '', pin: '', size: '', imagePreview: null, imageData: null });
    setShowVariantModal(true);
  };

  const handleAddVariant = async () => {
    if (!variantForm.name || !variantForm.price || !variantForm.pin) {
      toast.error("Name, price, and PIN are required"); return;
    }
    setActionLoading("variant");
    try {
      const payload = {
        name: variantForm.size || variantForm.name,
        price: parseFloat(variantForm.price),
        stock_quantity: parseInt(variantForm.stock_quantity) || 0,
        sku: variantForm.sku || null,
        pin: variantForm.pin,
        images: variantForm.imageData ? [variantForm.imageData] : []
      };
      await api.post(`/admin/products/${selectedProduct.id}/variants`, payload);
      toast.success("Variant added successfully");
      setShowVariantModal(false);
      setVariantForm({ name: "", price: "", stock_quantity: 0, sku: "", pin: "", size: "", imagePreview: null, imageData: null });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add variant");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleFreeShipping = async (productId, currentValue) => {
    setActionLoading(productId);
    try {
      await api.put(`/admin/products/${productId}/free-shipping?enabled=${!currentValue}`);
      toast.success(`Free shipping ${!currentValue ? 'enabled' : 'disabled'}`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update free shipping');
    } finally { setActionLoading(null); }
  };

  const handleToggleDiscount = async (productId, currentValue) => {
    try {
      await api.post(`/admin/products/${productId}/toggle-discount`, { eligible: !currentValue });
      toast.success(`Discount eligibility ${!currentValue ? 'enabled' : 'disabled'}`); fetchProducts();
    } catch (error) { toast.error('Failed to toggle discount eligibility'); }
  };

  const openHistoryModal = async (product) => {
    setSelectedProduct(product);
    try {
      const response = await api.get(`/admin/products/${product.id}/stock-history`);
      setStockHistory(response.data?.history || []); setShowHistoryModal(true);
    } catch (error) { toast.error('Failed to load stock history'); }
  };

  const handleBulkImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter(line => line.trim());
      if (lines.length < 2) {
        toast.error("CSV must have header row and at least one data row");
        return;
      }
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/['"]/g, ""));
      const preview = [];
      for (let i = 1; i < Math.min(lines.length, 6); i++) {
        const values = lines[i].split(",").map(v => v.trim().replace(/['"]/g, ""));
        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ""; });
        preview.push(row);
      }
      setBulkImportData({ file, preview, importing: false, results: null, totalRows: lines.length - 1 });
    };
    reader.readAsText(file);
  };

  const executeBulkImport = async () => {
    if (!bulkImportData.file) return;
    setBulkImportData(prev => ({ ...prev, importing: true }));
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter(line => line.trim());
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/['"]/g, ""));
      const products = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map(v => v.trim().replace(/['"]/g, ""));
        const product = {};
        headers.forEach((h, idx) => { 
          const val = values[idx] || "";
          if (["cost_price", "markup_percentage", "stock_quantity", "weight_lbs"].includes(h)) {
            product[h] = val ? parseFloat(val) : (h === "markup_percentage" ? 45 : undefined);
          } else {
            product[h] = val;
          }
        });
        if (product.name && product.cost_price && product.category) {
          products.push(product);
        }
      }
      try {
        const response = await api.post("/admin/products/bulk-import", products);
        setBulkImportData(prev => ({ ...prev, importing: false, results: response.data.results }));
        toast.success(response.data.message);
        fetchProducts();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Bulk import failed");
        setBulkImportData(prev => ({ ...prev, importing: false }));
      }
    };
    reader.readAsText(bulkImportData.file);
  };

  const downloadTemplate = () => {
    const template = "name,description,category,cost_price,markup_percentage,stock_quantity,weight_lbs,sku\nExample Product,A great product,Electronics,1000,50,25,2.5,ELEC-001";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "liantiao_product_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImageUpload = async (productId, event) => {
    const files = event.target.files;
    if (!files.length) return;
    const product = products.find(p => p.id === productId);
    if ((product?.images?.length || 0) + files.length > 4) {
      toast.error('Maximum 4 images allowed'); return;
    }
    setActionLoading(productId);
    try {
      const images = [];
      for (let file of files) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result); reader.readAsDataURL(file);
        });
        images.push(base64);
      }
      await api.post(`/admin/products/${productId}/images`, images);
      toast.success('Images uploaded'); fetchProducts();
    } catch (error) { toast.error('Failed to upload images'); }
    finally { setActionLoading(null); }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const stats = { 
    total: products.length, 
    lowStock: products.filter(p => (p.stock_quantity || 0) < 10).length,
    discountEligible: products.filter(p => p.is_discount_eligible).length,
    liantiaoProducts: products.filter(p => p.is_liantiao_product).length
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin')} variant="ghost" size="sm" data-testid="back-button"><ArrowLeft className="h-4 w-4" /></Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold">Liantiao</span>
              <span className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded border border-cyan-500/20">PRODUCTS</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowBulkImportModal(true)} variant="outline" data-testid="bulk-import-btn"><FileUp className="h-4 w-4 mr-2" />Bulk Import</Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-primary" data-testid="add-product-btn"><Plus className="h-4 w-4 mr-2" />Add Product</Button>
            <Button onClick={fetchProducts} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2"><Package className="h-5 w-5 text-muted-foreground" /><span className="text-muted-foreground text-sm">Total</span></div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2"><AlertTriangle className="h-5 w-5 text-yellow-400" /><span className="text-muted-foreground text-sm">Low Stock</span></div>
            <p className="text-2xl font-bold text-yellow-400">{stats.lowStock}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2"><Tag className="h-5 w-5 text-teal-400" /><span className="text-muted-foreground text-sm">Discount Eligible</span></div>
            <p className="text-2xl font-bold text-teal-400">{stats.discountEligible}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2"><ShoppingBag className="h-5 w-5 text-primary" /><span className="text-muted-foreground text-sm">Liantiao Products</span></div>
            <p className="text-2xl font-bold text-primary">{stats.liantiaoProducts}</p>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search products..." className="pl-10" data-testid="search-input" /></div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setCategoryFilter('all')} variant={categoryFilter === 'all' ? 'default' : 'outline'} size="sm">All</Button>
            {categories.map((cat) => (<Button key={cat} onClick={() => setCategoryFilter(cat)} variant={categoryFilter === cat ? 'default' : 'outline'} size="sm">{cat}</Button>))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (<div className="col-span-full p-8 text-center"><RefreshCw className="h-8 w-8 text-primary animate-spin mx-auto" /></div>
          ) : filteredProducts.length === 0 ? (<div className="col-span-full p-8 text-center text-muted-foreground">No products found</div>
          ) : (filteredProducts.map((product) => (
            <div key={product.id} className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-colors" data-testid={`product-card-${product.id}`}>
              <div className="relative h-40 bg-muted flex items-center justify-center">
                {product.images?.length > 0 ? <img src={product.images[0].startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` : product.images[0]} alt={product.name} className="w-full h-full object-cover"/>
                : <Package className="h-12 w-12 text-muted-foreground" />}
                
                {/* Liantiao Badge */}
                {product.is_liantiao_product && (
                  <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <Link2 className="h-3 w-3" /> Liantiao
                  </div>
                )}
                
                <div className="absolute top-2 right-2"><span className="px-2 py-0.5 rounded text-xs bg-black/50 text-white">{product.images?.length || 0}/4</span></div>
                <input type="file" accept="image/*" multiple className="hidden" id={`upload-${product.id}`} onChange={(e) => handleImageUpload(product.id, e)} />
                <label htmlFor={`upload-${product.id}`} className="absolute bottom-2 right-2 p-2 rounded bg-black/50 text-white cursor-pointer hover:bg-black/70"><Upload className="h-4 w-4" /></label>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-medium">{product.name}</h3>
                    <p className="text-xs text-muted-foreground font-mono">{product.sku}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-muted">{product.category}</span>
                </div>
                
                {/* Pricing Info */}
                <div className="mb-3 p-2 bg-muted/30 rounded">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-primary">J${product.price?.toFixed(2)}</span>
                    {product.original_price && product.original_price > product.price && (
                      <span className="text-sm text-muted-foreground line-through">J${product.original_price.toFixed(2)}</span>
                    )}
                  </div>
                  {product.cost_price > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Cost: J${product.cost_price?.toFixed(2)} | Markup: {product.markup_percentage || 45}%
                    </p>
                  )}
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${(product.stock_quantity || 0) < 10 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                      Stock: {product.stock_quantity || 0}
                    </span>
                    {product.startup_quantity && <span className="text-xs text-muted-foreground">(Start: {product.startup_quantity})</span>}
                    {product.weight_lbs && (
                      <span className="text-xs text-purple-400 flex items-center gap-1">
                        <Scale className="w-3 h-3" />
                        {product.weight_lbs} lbs
                      </span>
                    )}
                  </div>
                  <button 
                    onClick={() => handleToggleDiscount(product.id, product.is_discount_eligible)} 
                    className={`text-xs px-2 py-1 rounded border ${product.is_discount_eligible ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'bg-muted text-muted-foreground'}`}
                  >
                    {product.is_discount_eligible ? '✓ Discount' : '✗ No Discount'}
                  </button>
                </div>
                
                {/* Free Shipping Toggle */}
                <div className="flex items-center justify-between mb-3">
                  <button 
                    onClick={() => handleToggleFreeShipping(product.id, product.free_shipping)} 
                    className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${product.free_shipping ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-muted text-muted-foreground'}`}
                    disabled={actionLoading === product.id}
                  >
                    <Truck className="h-3 w-3" />
                    {product.free_shipping ? '✓ Free Shipping' : product.flat_rate_shipping ? `J$${product.flat_rate_shipping} Shipping` : 'No Free Shipping'}
                  </button>
                </div>
                
                {/* Wholesale Badge */}
                {product.is_wholesale_eligible && (
                  <div className="mb-3 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded border border-green-500/20">
                    Wholesale: 3+ = 10% | 9+ = 15% | 18+ = 25% off
                  </div>
                )}
                
                {product.variants?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Variants ({product.variants.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {product.variants.slice(0,3).map(v => <span key={v.id} className="text-xs px-2 py-0.5 rounded bg-muted">{v.name}: J${v.price}</span>)}
                      {product.variants.length > 3 && <span className="text-xs text-muted-foreground">+{product.variants.length - 3}</span>}
                    </div>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                  <div className="flex gap-2 flex-1">
                    <Button onClick={() => openStockModal(product, true)} variant="outline" size="sm" className="flex-1"><Plus className="h-3 w-3 mr-1"/>Stock</Button>
                    <Button onClick={() => openStockModal(product, false)} variant="outline" size="sm" className="flex-1"><Minus className="h-3 w-3 mr-1"/>Stock</Button>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => openVariantModal(product)} variant="outline" size="sm"><Layers className="h-3 w-3"/></Button>
                    <Button onClick={() => openHistoryModal(product)} variant="outline" size="sm"><History className="h-3 w-3"/></Button>
                    <Button onClick={() => handleEditProduct(product)} variant="outline" size="sm" className="text-blue-400 hover:bg-blue-500/10"><Edit2 className="h-3 w-3"/></Button>
                    <Button onClick={() => handleDeleteProduct(product.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" disabled={actionLoading === product.id}><Trash2 className="h-3 w-3"/></Button>
                  </div>
                </div>
              </div>
            </div>
          )))}
        </div>
      </main>

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2"><Package className="h-5 w-5 text-primary"/>{editingProduct ? 'Edit Product' : 'Add Liantiao Product'}</h2>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm"><X className="h-5 w-5"/></Button>
            </div>
            
            {/* Liantiao Badge */}
            <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">L</div>
              <div>
                <p className="font-semibold text-primary">Sold by Liantiao</p>
                <p className="text-xs text-muted-foreground">This product will display the Liantiao badge</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Product Images (Max 4) */}
              <div>
                <Label className="flex items-center gap-2"><Camera className="h-4 w-4"/> Product Images (Max 4)</Label>
                <div className="flex gap-3 mt-2 flex-wrap">
                  {(newProduct.images || []).map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img} alt={`Product ${idx+1}`} className="w-20 h-20 rounded-lg object-cover border border-border"/>
                      <button onClick={() => setNewProduct({...newProduct, images: newProduct.images.filter((_, i) => i !== idx)})} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X className="h-3 w-3"/></button>
                    </div>
                  ))}
                  {(newProduct.images?.length || 0) < 4 && (
                    <label className="flex items-center justify-center w-20 h-20 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = () => setNewProduct({...newProduct, images: [...(newProduct.images || []), reader.result]});
                          reader.readAsDataURL(file);
                        }
                      }}/>
                      <div className="text-center"><Upload className="h-5 w-5 mx-auto text-muted-foreground"/><span className="text-xs text-muted-foreground">{(newProduct.images?.length || 0)}/4</span></div>
                    </label>
                  )}
                </div>
              </div>
              
              <div><Label>Name *</Label><Input value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} placeholder="Product name" data-testid="product-name-input"/></div>
              <div><Label>Description</Label><textarea value={newProduct.description} onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} className="w-full bg-background border border-input rounded-md p-2 text-sm" rows={2} placeholder="Describe your product..."/></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category *</Label><Input value={newProduct.category} onChange={(e) => setNewProduct({...newProduct, category: e.target.value})} list="categories" placeholder="Electronics, Clothing..." data-testid="product-category-input"/><datalist id="categories">{categories.map(c => <option key={c} value={c}/>)}</datalist></div>
                <div><Label>SKU (auto if blank)</Label><Input value={newProduct.sku} onChange={(e) => setNewProduct({...newProduct, sku: e.target.value.toUpperCase()})} placeholder="AUTO-GEN"/></div>
              </div>
              
              {/* Pricing Section - MAIN FEATURE */}
              <div className="p-4 bg-gradient-to-r from-primary/10 to-transparent border border-primary/30 rounded-lg space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-primary"><DollarSign className="h-5 w-5"/>Pricing Calculator</h3>
                <p className="text-xs text-muted-foreground">Selling Price = Cost Price × (1 + Markup%)</p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-primary">Cost Price * (J$)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={newProduct.cost_price} 
                      onChange={(e) => {
                        const cost = e.target.value;
                        const selling = calculateSellingPrice(cost, newProduct.markup_percentage);
                        setNewProduct({...newProduct, cost_price: cost, price: selling.toString()});
                      }} 
                      placeholder="0.00"
                      className="border-primary/50"
                      data-testid="cost-price-input"
                    />
                  </div>
                  <div>
                    <Label>Markup %</Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="number" 
                        value={newProduct.markup_percentage} 
                        onChange={(e) => {
                          const markup = e.target.value;
                          const selling = calculateSellingPrice(newProduct.cost_price, markup);
                          setNewProduct({...newProduct, markup_percentage: parseFloat(markup) || 45, price: selling.toString()});
                        }} 
                        className="w-20"
                        data-testid="markup-input"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-green-400">Selling Price (J$)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={newProduct.price} 
                      readOnly
                      className="bg-green-500/10 border-green-500/30 font-bold text-green-400"
                      data-testid="selling-price-display"
                    />
                  </div>
                </div>
                
                {/* Live Preview */}
                {newProduct.cost_price && (
                  <div className="mt-3 p-3 bg-background/50 rounded border border-border">
                    <p className="text-sm font-medium mb-2">Price Preview:</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Cost: <span className="text-muted-foreground">J${parseFloat(newProduct.cost_price || 0).toFixed(2)}</span></div>
                      <div>Markup: <span className="text-muted-foreground">{newProduct.markup_percentage}%</span></div>
                      <div>Profit per unit: <span className="text-green-400">J${(parseFloat(newProduct.price || 0) - parseFloat(newProduct.cost_price || 0)).toFixed(2)}</span></div>
                      <div>Selling: <span className="text-primary font-bold">J${parseFloat(newProduct.price || 0).toFixed(2)}</span></div>
                    </div>
                  </div>
                )}
              </div>
              
              <div><Label>Original Price (for showing discount strike-through)</Label><Input type="number" step="0.01" value={newProduct.original_price} onChange={(e) => setNewProduct({...newProduct, original_price: e.target.value})} placeholder="Higher than selling = shows discount badge"/></div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Stock Quantity</Label><Input type="number" value={newProduct.stock_quantity} onChange={(e) => setNewProduct({...newProduct, stock_quantity: e.target.value})} placeholder="0"/></div>
                <div><Label>Startup Qty (tracking)</Label><Input type="number" value={newProduct.startup_quantity} onChange={(e) => setNewProduct({...newProduct, startup_quantity: e.target.value})} placeholder="Initial stock"/></div>
              </div>
              
              {/* Weight for Shipping */}
              <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="h-4 w-4 text-purple-400" />
                  <Label className="font-medium">Weight (lbs)</Label>
                  <span className="text-xs text-muted-foreground">(optional)</span>
                </div>
                <Input 
                  type="number" 
                  step="0.1" 
                  min="0" 
                  max="200"
                  value={newProduct.weight_lbs} 
                  onChange={(e) => setNewProduct({...newProduct, weight_lbs: e.target.value})} 
                  placeholder="e.g. 2.5"
                  className="w-40"
                  data-testid="product-weight-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Used to calculate accurate shipping at checkout</p>
              </div>
              
              
              {/* Product Condition */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Product Condition</Label>
                <div className="flex flex-wrap gap-2">
                  {["NEW", "OPEN_BOX", "REFURBISHED", "USED"].map((cond) => (
                    <button
                      key={cond}
                      type="button"
                      onClick={() => setNewProduct({...newProduct, condition: cond})}
                      className={`px-3 py-2 rounded-lg text-sm border transition-all ${newProduct.condition === cond ? "bg-primary text-primary-foreground border-primary" : "bg-muted/30 border-border hover:border-primary/50"}`}
                    >
                      {cond.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sale Mode / Auction Settings */}
              <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Gavel className="h-4 w-4 text-amber-500" />
                  <Label className="font-medium">Selling Type</Label>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { value: "FIXED_PRICE", label: "Fixed Price" },
                    { value: "AUCTION", label: "Auction Only" },
                    { value: "FIXED_OR_AUCTION", label: "Auction + Buy Now" }
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setNewProduct({...newProduct, sale_mode: mode.value})}
                      className={`px-3 py-2 rounded-lg text-sm border transition-all ${newProduct.sale_mode === mode.value ? "bg-amber-500 text-white border-amber-500" : "bg-muted/30 border-border hover:border-amber-500/50"}`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>

                {/* Auction Settings - Show when auction enabled */}
                {(newProduct.sale_mode === "AUCTION" || newProduct.sale_mode === "FIXED_OR_AUCTION") && (
                  <div className="space-y-4 pt-3 border-t border-amber-500/20">
                    <div className="flex items-center gap-2 text-amber-500">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">Auction Settings</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Auction Start</Label>
                        <Input
                          type="datetime-local"
                          value={newProduct.auction_start_at}
                          onChange={(e) => setNewProduct({...newProduct, auction_start_at: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Auction End</Label>
                        <Input
                          type="datetime-local"
                          value={newProduct.auction_end_at}
                          onChange={(e) => setNewProduct({...newProduct, auction_end_at: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Min Starting Bid (JMD)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 1000"
                          value={newProduct.auction_min_bid_jmd}
                          onChange={(e) => setNewProduct({...newProduct, auction_min_bid_jmd: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Bid Increment (JMD)</Label>
                        <Input
                          type="number"
                          placeholder="100"
                          value={newProduct.auction_min_increment_jmd}
                          onChange={(e) => setNewProduct({...newProduct, auction_min_increment_jmd: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    {newProduct.sale_mode === "FIXED_OR_AUCTION" && (
                      <div>
                        <Label className="text-xs">Buy Now Price (JMD)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 5000"
                          value={newProduct.buy_now_price_jmd}
                          onChange={(e) => setNewProduct({...newProduct, buy_now_price_jmd: e.target.value})}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Price to instantly purchase without bidding</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Wholesale Section */}}
              <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <input type="checkbox" id="wholesale" checked={newProduct.is_wholesale_eligible} onChange={(e) => setNewProduct({...newProduct, is_wholesale_eligible: e.target.checked})} className="w-4 h-4 rounded"/>
                  <Label htmlFor="wholesale" className="cursor-pointer font-medium">Enable Wholesale Pricing</Label>
                </div>
                {newProduct.is_wholesale_eligible && (
                  <div className="text-xs space-y-1 text-green-400">
                    <p className="font-medium">Automatic Discount Tiers:</p>
                    <div className="flex gap-4">
                      {WHOLESALE_TIERS.map(t => (
                        <span key={t.qty} className="px-2 py-1 bg-green-500/20 rounded">{t.qty}+ items = {t.discount}% off</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <input type="checkbox" id="disc" checked={newProduct.is_discount_eligible} onChange={(e) => setNewProduct({...newProduct, is_discount_eligible: e.target.checked})} className="w-4 h-4 rounded"/>
                <Label htmlFor="disc" className="cursor-pointer">Eligible for site-wide discounts & promotions</Label>
              </div>
              
              {/* Free Shipping Toggle */}
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <input type="checkbox" id="freeShip" checked={newProduct.free_shipping} onChange={(e) => setNewProduct({...newProduct, free_shipping: e.target.checked, flat_rate_shipping: e.target.checked ? '' : newProduct.flat_rate_shipping})} className="w-4 h-4 rounded accent-green-500"/>
                <div className="flex-1">
                  <Label htmlFor="freeShip" className="cursor-pointer flex items-center gap-2">
                    <Truck className="h-4 w-4 text-green-500" />
                    Free Delivery on this item
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">Enable to show "Free Delivery" badge on this product</p>
                </div>
              </div>
              
              {/* Flat Rate Shipping - Only show when not free shipping */}
              {!newProduct.free_shipping && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <Label className="flex items-center gap-2 mb-2">
                    <Truck className="h-4 w-4 text-amber-500" />
                    Flat Rate Shipping (JMD)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter shipping cost (e.g., 500)"
                    value={newProduct.flat_rate_shipping}
                    onChange={(e) => setNewProduct({...newProduct, flat_rate_shipping: e.target.value})}
                    className="bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fixed shipping cost for this product. Leave empty to use weight-based shipping.
                  </p>
                </div>
              )}
            </div>
              {/* Product Video Section */}
              {editingProduct ? (
                <div className="mt-4">
                  <ProductVideoUpload
                    productId={editingProduct.id}
                    videos={productVideos}
                    onVideoChange={setProductVideos}
                  />
                </div>
              ) : (
                <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg mt-4">
                  <div className="flex items-center gap-2 text-base font-semibold mb-3">
                    <Video className="h-4 w-4 text-emerald-500" />
                    Product Video
                    <span className="text-xs font-normal text-muted-foreground border border-border rounded px-2 py-0.5">Optional</span>
                  </div>
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center">
                    <Video className="h-10 w-10 mx-auto mb-3 text-slate-500" />
                    <p className="text-sm font-medium text-slate-300 mb-1">Video Upload Available After Save</p>
                    <p className="text-xs text-muted-foreground">Save your product first, then edit to add a video</p>
                  </div>
                </div>
              )}

            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowAddModal(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleAddProduct} className="flex-1 bg-primary" disabled={actionLoading === "add"} data-testid="submit-product-btn">
                {actionLoading === "add" ? <RefreshCw className="h-4 w-4 animate-spin mr-2"/> : <Plus className="h-4 w-4 mr-2"/>}
                {editingProduct ? 'Update Product' : 'Add Product'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Modal */}
      {showStockModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {stockForm.isAdding ? <Plus className="h-5 w-5 inline text-teal-400"/> : <Minus className="h-5 w-5 inline text-red-400"/>} 
                {stockForm.isAdding ? 'Add' : 'Remove'} Stock
              </h2>
              <Button onClick={() => setShowStockModal(false)} variant="ghost" size="sm"><X className="h-5 w-5"/></Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Product: <strong>{selectedProduct.name}</strong> | Current: <strong>{selectedProduct.stock_quantity || 0}</strong></p>
            <div className="space-y-4">
              <div><Label>Quantity *</Label><Input type="number" value={stockForm.quantity} onChange={(e) => setStockForm({...stockForm, quantity: e.target.value})} min="1"/></div>
              <div><Label>Reason *</Label><Input value={stockForm.reason} onChange={(e) => setStockForm({...stockForm, reason: e.target.value})} placeholder="e.g., New shipment"/></div>
              <div><Label>Your PIN *</Label><Input type="password" value={stockForm.pin} onChange={(e) => setStockForm({...stockForm, pin: e.target.value})}/></div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowStockModal(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleStockAdjust} className={`flex-1 ${stockForm.isAdding ? 'bg-teal-500' : 'bg-red-500'}`} disabled={actionLoading === 'stock'}>
                {actionLoading === 'stock' && <RefreshCw className="h-4 w-4 animate-spin mr-2"/>}
                {stockForm.isAdding ? 'Add' : 'Remove'} Stock
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Variant Modal */}
      {showVariantModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold"><Layers className="h-5 w-5 inline text-primary"/> Add Variant</h2>
              <Button onClick={() => setShowVariantModal(false)} variant="ghost" size="sm"><X className="h-5 w-5"/></Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Product: <strong>{selectedProduct.name}</strong></p>
            
            {/* Existing Variants */}
            {selectedProduct.variants?.length > 0 && (
              <div className="mb-6 p-3 bg-muted/30 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2">Existing Variants ({selectedProduct.variants.length})</p>
                <div className="flex flex-wrap gap-2">
                  {selectedProduct.variants.map(v => (
                    <div key={v.id} className="flex items-center gap-2 px-2 py-1 bg-background rounded text-xs">
                      {v.images?.[0] && <img src={v.images[0].startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${v.images[0]}` : v.images[0]} className="w-6 h-6 rounded object-cover" alt=""/>}
                      <span>{v.name}</span>
                      <span className="text-primary">J${v.price}</span>
                      <span className="text-muted-foreground">({v.stock_quantity})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Variant Name *</Label><Input value={variantForm.name} onChange={(e) => setVariantForm({...variantForm, name: e.target.value})} placeholder="e.g., Red, Large, 500ml"/></div>
                <div><Label>Size (optional)</Label>
                  <select value={variantForm.size || ''} onChange={(e) => setVariantForm({...variantForm, size: e.target.value, name: e.target.value || variantForm.name})} className="w-full h-10 px-3 bg-background border border-input rounded-md text-sm">
                    <option value="">Select size...</option>
                    <option value="XS">XS - Extra Small</option>
                    <option value="S">S - Small</option>
                    <option value="M">M - Medium</option>
                    <option value="L">L - Large</option>
                    <option value="XL">XL - Extra Large</option>
                    <option value="XXL">XXL</option>
                    <option value="One Size">One Size</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Price * (J$)</Label><Input type="number" step="0.01" value={variantForm.price} onChange={(e) => setVariantForm({...variantForm, price: e.target.value})}/></div>
                <div><Label>Stock Quantity</Label><Input type="number" value={variantForm.stock_quantity} onChange={(e) => setVariantForm({...variantForm, stock_quantity: e.target.value})}/></div>
              </div>
              
              <div><Label>SKU (auto-generated if blank)</Label><Input value={variantForm.sku} onChange={(e) => setVariantForm({...variantForm, sku: e.target.value.toUpperCase()})} placeholder="Leave blank for auto"/></div>
              
              {/* Variant Image Upload */}
              <div>
                <Label>Variant Image (optional)</Label>
                <div className="mt-2 flex items-center gap-4">
                  {variantForm.imagePreview ? (
                    <div className="relative">
                      <img src={variantForm.imagePreview} alt="Preview" className="w-20 h-20 rounded-lg object-cover border border-border"/>
                      <button onClick={() => setVariantForm({...variantForm, imagePreview: null, imageData: null})} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X className="h-3 w-3"/></button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center w-20 h-20 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = () => setVariantForm({...variantForm, imagePreview: reader.result, imageData: reader.result});
                          reader.readAsDataURL(file);
                        }
                      }}/>
                      <Upload className="h-6 w-6 text-muted-foreground"/>
                    </label>
                  )}
                  <div className="text-xs text-muted-foreground">
                    <p>Upload an image for this variant</p>
                    <p>e.g., different color photo</p>
                  </div>
                </div>
              </div>
              
              <div><Label>Your PIN *</Label><Input type="password" value={variantForm.pin} onChange={(e) => setVariantForm({...variantForm, pin: e.target.value})} placeholder="Enter your PIN to confirm"/></div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowVariantModal(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleAddVariant} className="flex-1 bg-primary" disabled={actionLoading === 'variant'}>
                {actionLoading === 'variant' && <RefreshCw className="h-4 w-4 animate-spin mr-2"/>}
                Add Variant
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[80vh] overflow-hidden p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold"><History className="h-5 w-5 inline text-primary"/> Stock History</h2>
              <Button onClick={() => setShowHistoryModal(false)} variant="ghost" size="sm"><X className="h-5 w-5"/></Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Product: <strong>{selectedProduct.name}</strong></p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {stockHistory.length === 0 ? <p className="text-center text-muted-foreground py-8">No history</p> :
              stockHistory.map(e => (
                <div key={e.id} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-mono text-sm ${e.quantity_change > 0 ? 'text-teal-400' : 'text-red-400'}`}>
                      {e.quantity_change > 0 ? '+' : ''}{e.quantity_change}
                    </span>
                    <span className="text-xs text-muted-foreground">{new Date(e.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-sm">{e.reason}</p>
                  <p className="text-xs text-muted-foreground">By: {e.user_email} | {e.previous_quantity} → {e.new_quantity}</p>
                </div>
              ))}
            </div>
            <div className="mt-6"><Button onClick={() => setShowHistoryModal(false)} variant="outline" className="w-full">Close</Button></div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold"><FileUp className="h-5 w-5 inline text-primary mr-2"/>Bulk Product Import</h2>
              <Button onClick={() => { setShowBulkImportModal(false); setBulkImportData({ file: null, preview: [], importing: false, results: null }); }} variant="ghost" size="sm"><X className="h-5 w-5"/></Button>
            </div>
            
            {!bulkImportData.results ? (
              <>
                <div className="mb-6 p-4 bg-muted/30 rounded-lg">
                  <h3 className="font-medium mb-2">CSV Format Requirements</h3>
                  <p className="text-sm text-muted-foreground mb-3">Upload a CSV file with the following columns:</p>
                  <div className="text-xs space-y-1 font-mono bg-background p-3 rounded border border-border">
                    <p><span className="text-primary">name*</span> - Product name (required)</p>
                    <p><span className="text-primary">category*</span> - Category name (required)</p>
                    <p><span className="text-primary">cost_price*</span> - Cost price in JMD (required)</p>
                    <p><span className="text-muted-foreground">description</span> - Product description</p>
                    <p><span className="text-muted-foreground">markup_percentage</span> - Markup % (default: 45)</p>
                    <p><span className="text-muted-foreground">stock_quantity</span> - Initial stock (default: 0)</p>
                    <p><span className="text-muted-foreground">weight_lbs</span> - Weight in pounds</p>
                    <p><span className="text-muted-foreground">sku</span> - SKU (auto-generated if blank)</p>
                  </div>
                  <Button onClick={downloadTemplate} variant="outline" size="sm" className="mt-3">
                    <Download className="h-4 w-4 mr-2"/>Download Template
                  </Button>
                </div>
                
                <div className="mb-6">
                  <Label>Upload CSV File</Label>
                  <div className="mt-2">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors bg-muted/20">
                      <input type="file" accept=".csv" className="hidden" onChange={handleBulkImportFile}/>
                      <FileUp className="h-8 w-8 text-muted-foreground mb-2"/>
                      <span className="text-sm text-muted-foreground">{bulkImportData.file ? bulkImportData.file.name : "Click to upload CSV file"}</span>
                    </label>
                  </div>
                </div>
                
                {bulkImportData.preview.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium mb-2">Preview ({bulkImportData.totalRows} rows)</h3>
                    <div className="overflow-x-auto border border-border rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr>
                            {Object.keys(bulkImportData.preview[0]).map(key => (
                              <th key={key} className="px-3 py-2 text-left font-medium">{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bulkImportData.preview.map((row, idx) => (
                            <tr key={idx} className="border-t border-border">
                              {Object.values(row).map((val, vidx) => (
                                <td key={vidx} className="px-3 py-2 max-w-32 truncate">{val}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {bulkImportData.totalRows > 5 && (
                      <p className="text-xs text-muted-foreground mt-2">Showing first 5 of {bulkImportData.totalRows} rows</p>
                    )}
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button onClick={() => { setShowBulkImportModal(false); setBulkImportData({ file: null, preview: [], importing: false, results: null }); }} variant="outline" className="flex-1">Cancel</Button>
                  <Button onClick={executeBulkImport} className="flex-1 bg-primary" disabled={!bulkImportData.file || bulkImportData.importing}>
                    {bulkImportData.importing ? <RefreshCw className="h-4 w-4 animate-spin mr-2"/> : <FileUp className="h-4 w-4 mr-2"/>}
                    Import Products
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-teal-500/10 border border-teal-500/20 rounded-lg text-center">
                    <p className="text-3xl font-bold text-teal-400">{bulkImportData.results.success.length}</p>
                    <p className="text-sm text-muted-foreground">Imported Successfully</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-400">{bulkImportData.results.failed.length}</p>
                    <p className="text-sm text-muted-foreground">Failed</p>
                  </div>
                </div>
                
                {bulkImportData.results.failed.length > 0 && (
                  <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <h4 className="font-medium text-red-400 mb-2">Failed Items:</h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {bulkImportData.results.failed.map((item, idx) => (
                        <p key={idx} className="text-xs text-red-400">Row {item.index + 2}: {item.name || "Unknown"} - {item.error}</p>
                      ))}
                    </div>
                  </div>
                )}
                
                {bulkImportData.results.success.length > 0 && (
                  <div className="p-4 bg-teal-500/5 border border-teal-500/20 rounded-lg">
                    <h4 className="font-medium text-teal-400 mb-2">Imported Products:</h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {bulkImportData.results.success.slice(0, 10).map((item, idx) => (
                        <p key={idx} className="text-xs">✓ {item.name} (SKU: {item.sku}, J${item.price})</p>
                      ))}
                      {bulkImportData.results.success.length > 10 && (
                        <p className="text-xs text-muted-foreground">...and {bulkImportData.results.success.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}
                
                <Button onClick={() => { setShowBulkImportModal(false); setBulkImportData({ file: null, preview: [], importing: false, results: null }); }} className="w-full">Close</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsManagement;
